/**
 * offline-queue — E6-S1
 *
 * A tiny IndexedDB-backed FIFO queue used by the Batch Grade Grid so cadre
 * can score trainees while the browser is offline. Each item represents one
 * pending network write (e.g. a POST /attempts payload) plus its status.
 * When the browser is online, the queue can be drained by handing each item
 * to a caller-provided async worker.
 *
 * Design notes (v1 pattern port):
 *   - v1 used react-query + persistQueryClient-experimental + AsyncStorage +
 *     react-native-bluetooth-classic. See docs/E6-S1-PORTING-NOTES.md.
 *   - The web port keeps the "persist + sync-on-reconnect" half of the v1
 *     pattern; Bluetooth peer-to-peer sync is out of scope (Web Bluetooth
 *     is BLE-only; v1's Classic RFCOMM has no web equivalent).
 *   - No external runtime dependency — uses the browser's IndexedDB API
 *     directly. Tests use fake-indexeddb to give jsdom an IndexedDB.
 *
 * Item lifecycle:
 *   queued   — enqueued, waiting for network
 *   syncing  — currently being handed to the worker
 *   synced   — worker returned successfully; kept briefly for UI, then
 *              swept via deleteSynced()
 *   error    — worker threw; retained with error message
 *
 * Concurrency: `drain()` is safe to call repeatedly when workers use the
 * stable operation id for server idempotency. An item left `syncing` by a
 * session/owner change remains deliberately eligible for the original owner
 * to retry; no stale owner mutation is needed to restore it. Two concurrent
 * drains can still attempt the same item, so workers must be idempotent.
 */

export type QueueItemStatus = 'queued' | 'syncing' | 'synced' | 'error' | 'conflict' | 'authentication' | 'blocked';

export interface QueueItem<T = unknown> {
  id: string;              // scope-bound IndexedDB storage key
  operationId?: string;    // stable caller-supplied operation id
  bindingVersion?: 1;      // absent on quarantined legacy records
  payload: T;              // opaque payload the worker consumes
  status: QueueItemStatus;
  createdAt: number;       // Date.now()
  updatedAt: number;
  attempts: number;
  errorMessage?: string;
  /** Earliest time an automatic retry may run after a transient error. */
  retryAfter?: number;
  /** Verified tenant + actor + instance + event scope. */
  scope?: string;
}

export interface QueueAccess {
  scope: string;
  sessionToken: string;
  isCurrent: () => boolean;
}

const DEFAULT_DB_NAME = 'chamber-offline';
const DEFAULT_STORE_NAME = 'batch-grade-queue';
const DB_VERSION = 1;

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(
  dbName: string = DEFAULT_DB_NAME,
  storeName: string = DEFAULT_STORE_NAME,
): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(storeName)) {
        const store = db.createObjectStore(storeName, { keyPath: 'id' });
        store.createIndex('status', 'status');
        store.createIndex('scope', 'scope');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
  return _dbPromise;
}

/** Reset the module-level DB promise. Test-only. */
export function _resetForTests(): void {
  _dbPromise = null;
}

function txPromise<T>(
  request: IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IDB request failed'));
  });
}

export function isQueueAccessCurrent(access?: QueueAccess): access is QueueAccess {
  if (!access?.scope.trim() || !access.sessionToken) return false;
  try {
    return access.isCurrent();
  } catch {
    return false;
  }
}

const storageId = (scope: string, operationId: string): string =>
  JSON.stringify([scope, operationId]);

const canonicalPayload = (value: unknown): string => {
  const normalise = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(normalise);
    if (input && typeof input === 'object') {
      return Object.fromEntries(
        Object.entries(input as Record<string, unknown>)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([key, child]) => [key, normalise(child)]),
      );
    }
    return input;
  };
  return JSON.stringify(normalise(value));
};

/** Enqueue a new item. Returns the item as stored. */
export async function enqueue<T>(
  payload: T,
  opts: { id?: string; access?: QueueAccess } = {},
): Promise<QueueItem<T>> {
  if (!isQueueAccessCurrent(opts.access)) {
    throw new Error('A verified current capture session is required to queue data');
  }
  const access = opts.access;
  const db = await openDb();
  if (!isQueueAccessCurrent(access)) {
    throw new Error('Capture session changed while opening the offline queue');
  }
  const operationId = opts.id ?? crypto.randomUUID();
  if (!operationId.trim()) throw new Error('A non-empty operation id is required');
  const id = storageId(access.scope, operationId);
  const now = Date.now();
  const item: QueueItem<T> = {
    id,
    operationId,
    bindingVersion: 1,
    payload,
    status: 'queued',
    createdAt: now,
    updatedAt: now,
    attempts: 0,
    scope: access.scope,
  };
  const tx = db.transaction(DEFAULT_STORE_NAME, 'readwrite');
  const store = tx.objectStore(DEFAULT_STORE_NAME);
  const existing = await txPromise(
    store.get(id) as IDBRequest<QueueItem<T> | undefined>,
  );
  if (!isQueueAccessCurrent(access)) {
    throw new Error('Capture session changed while reading the offline queue');
  }
  if (existing) {
    if (
      existing.bindingVersion !== 1 ||
      existing.scope !== access.scope ||
      existing.operationId !== operationId
    ) {
      throw new Error('Queued operation binding is invalid');
    }
    if (canonicalPayload(existing.payload) !== canonicalPayload(payload)) {
      throw new Error('A queued operation payload cannot change');
    }
    return existing;
  }
  store.put(item);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
  if (!isQueueAccessCurrent(access)) {
    throw new Error('Capture session changed while writing the offline queue');
  }
  return item;
}

/** Return only records bound to the verified current capture scope. */
export async function list(access?: QueueAccess): Promise<QueueItem[]> {
  if (!isQueueAccessCurrent(access)) return [];
  const db = await openDb();
  if (!isQueueAccessCurrent(access)) return [];
  const tx = db.transaction(DEFAULT_STORE_NAME, 'readonly');
  const all = await txPromise(tx.objectStore(DEFAULT_STORE_NAME).getAll() as IDBRequest<QueueItem[]>);
  if (!isQueueAccessCurrent(access)) return [];
  return all.filter((item) =>
    item.bindingVersion === 1 &&
    item.scope === access.scope &&
    typeof item.operationId === 'string' &&
    item.operationId.length > 0);
}

/** Return a single item by id, or undefined. */
export async function get(
  operationId: string,
  access?: QueueAccess,
): Promise<QueueItem | undefined> {
  if (!isQueueAccessCurrent(access)) return undefined;
  const db = await openDb();
  if (!isQueueAccessCurrent(access)) return undefined;
  const tx = db.transaction(DEFAULT_STORE_NAME, 'readonly');
  const item = await txPromise(
    tx.objectStore(DEFAULT_STORE_NAME).get(storageId(access.scope, operationId)) as
      IDBRequest<QueueItem | undefined>,
  );
  if (!isQueueAccessCurrent(access)) return undefined;
  return item?.bindingVersion === 1 && item.scope === access.scope &&
    item.operationId === operationId ? item : undefined;
}

/** Persist a status/error update for an item. */
export async function update(
  operationId: string,
  patch: Partial<Pick<QueueItem, 'status' | 'errorMessage' | 'retryAfter'>>,
  access?: QueueAccess,
): Promise<boolean> {
  if (!isQueueAccessCurrent(access)) return false;
  const db = await openDb();
  if (!isQueueAccessCurrent(access)) return false;
  const tx = db.transaction(DEFAULT_STORE_NAME, 'readwrite');
  const store = tx.objectStore(DEFAULT_STORE_NAME);
  const id = storageId(access.scope, operationId);
  const current = await txPromise(store.get(id) as IDBRequest<QueueItem | undefined>);
  if (!isQueueAccessCurrent(access)) return false;
  if (
    !current || current.bindingVersion !== 1 ||
    current.scope !== access.scope || current.operationId !== operationId
  ) return false;
  const next: QueueItem = {
    ...current,
    ...patch,
    updatedAt: Date.now(),
    attempts: patch.status === 'syncing' ? current.attempts + 1 : current.attempts,
  };
  store.put(next);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
  return isQueueAccessCurrent(access);
}

/** Delete a single item (used after a successful sync). */
export async function remove(
  operationId: string,
  access?: QueueAccess,
): Promise<boolean> {
  if (!isQueueAccessCurrent(access)) return false;
  const db = await openDb();
  if (!isQueueAccessCurrent(access)) return false;
  const tx = db.transaction(DEFAULT_STORE_NAME, 'readwrite');
  const store = tx.objectStore(DEFAULT_STORE_NAME);
  const id = storageId(access.scope, operationId);
  const current = await txPromise(store.get(id) as IDBRequest<QueueItem | undefined>);
  if (!isQueueAccessCurrent(access)) return false;
  if (
    !current || current.bindingVersion !== 1 ||
    current.scope !== access.scope || current.operationId !== operationId
  ) return false;
  store.delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
  return isQueueAccessCurrent(access);
}

/**
 * Sweep all items in `synced` state. Useful to keep IndexedDB tidy after a
 * batch has been confirmed on-screen.
 */
export async function deleteSynced(access?: QueueAccess): Promise<number> {
  if (!isQueueAccessCurrent(access)) return 0;
  const items = await list(access);
  let n = 0;
  for (const item of items) {
    if (!isQueueAccessCurrent(access)) break;
    if (item.status === 'synced') {
      if (await remove(item.operationId!, access)) n++;
    }
  }
  return n;
}

type DrainResult = { attempted: number; synced: number; errored: number };
const activeDrains = new Map<string, Promise<DrainResult>>();

/**
 * Drain eligible items via the caller's worker. `automatic` drains skip items
 * waiting on backoff or on re-authentication and never retry conflicts or
 * blocked items. A session change leaves the exact item in retriable `syncing`
 * state for its original owner. Drains are serialized per scope within a tab only;
 * the worker must be idempotent because a server commit can outlive a lost
 * response and another tab may attempt the same item.
 */
export async function drain<T = unknown>(
  worker: (payload: T, operationId: string, access: QueueAccess) => Promise<void>,
  access?: QueueAccess,
  opts: { automatic?: boolean } = {},
): Promise<DrainResult> {
  if (!isQueueAccessCurrent(access)) return { attempted: 0, synced: 0, errored: 0 };
  const key = access.scope;
  const active = activeDrains.get(key);
  if (active) return active;
  const run = async (): Promise<DrainResult> => {
    if (!isQueueAccessCurrent(access)) return { attempted: 0, synced: 0, errored: 0 };
    const items = await list(access);
    if (!isQueueAccessCurrent(access)) return { attempted: 0, synced: 0, errored: 0 };
    const eligible = items
      .filter((item) =>
        ['queued', 'error', 'syncing'].includes(item.status) ||
        (!opts.automatic && item.status === 'authentication'))
      .filter((item) =>
        !opts.automatic || !item.retryAfter || item.retryAfter <= Date.now() ||
        Date.now() < item.updatedAt)
      .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
    let attempted = 0;
    let synced = 0;
    let errored = 0;
    for (const item of eligible) {
      if (!isQueueAccessCurrent(access)) break;
      const operationId = item.operationId!;
      if (!await update(operationId, { status: 'syncing' }, access)) break;
      if (!isQueueAccessCurrent(access)) {
        await update(operationId, { status: 'queued' }, access);
        break;
      }
      attempted++;
      try {
        await worker(item.payload as T, operationId, access);
        if (!isQueueAccessCurrent(access)) break;
        if (!await update(
          operationId,
          { status: 'synced', errorMessage: undefined, retryAfter: undefined },
          access,
        )) break;
        synced++;
      } catch (err) {
        if (!isQueueAccessCurrent(access)) break;
        const message = err instanceof Error ? err.message : String(err);
        const failure = err as { status?: number; code?: string };
        const status: QueueItemStatus = failure?.status === 401 ? 'authentication' :
          failure?.status === 409 && failure.code !== 'CAPTURE_RETRY' ? 'conflict' :
          [400, 403, 404, 413, 422].includes(failure?.status ?? 0) ? 'blocked' : 'error';
        if (!await update(operationId, {
          status,
          errorMessage: message,
          retryAfter: status === 'error'
            ? Date.now() + Math.min(300000, 30000 * 2 ** Math.min(item.attempts, 4))
            : undefined,
        }, access)) break;
        errored++;
      }
    }
    return { attempted, synced, errored };
  };
  const result = run();
  activeDrains.set(key, result);
  try { return await result; } finally { activeDrains.delete(key); }
}
